import { forwardRef, useState } from 'react';
import { InputWrapper, type BaseInputProps } from './InputWrapper';
import { cn } from '~/utils';

type TextAreaProps = BaseInputProps & {
  rows?: number;
  maxLength?: number;
  describedBy?: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, ref) => {
    const [focus, setFocus] = useState(false);
    const errorId = `${props.id}-error`;
    const hasError = props.error !== undefined && props.error !== '';
    const describedBy =
      [hasError ? errorId : undefined, props.describedBy]
        .filter(Boolean)
        .join(' ') || undefined;

    const onFocus = () => {
      setFocus(true);
      props.onFocus?.();
    };

    const onBlur = () => {
      props.onBlur?.();
      setFocus(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const name = e.target.name;
      const value = e.target.value;
      props.onChange?.(name, value);
    };

    return (
      <InputWrapper
        label={props.label}
        id={props.id}
        error={props.error}
        required={props.required}
        disabled={props.disabled}
        readOnly={props.readOnly}
        containerClassName={props.containerClassName}
        focus={focus}
        leftIcon={props.leftIcon}
        rightIcon={props.rightIcon}
      >
        <textarea
          className={cn(
            'bg-none border-none focus:outline-none hover:outline-none py-2 px-4 text-sm leading-5 font-normal w-full resize-none',
            props.rightAlign ? 'text-right' : ''
          )}
          id={props.id}
          name={props.name}
          value={props.value}
          onChange={handleChange}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readOnly={props.readOnly}
          onFocus={onFocus}
          onBlur={onBlur}
          onPaste={props.onPaste}
          aria-disabled={props.disabled}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          aria-required={props.required}
          tabIndex={props.disabled ? -1 : 0}
          onKeyDown={props.onKeyDown}
          ref={ref}
          data-cy={props.dataCy}
          rows={props.rows || 4}
          maxLength={props.maxLength}
        />
      </InputWrapper>
    );
  }
);

TextArea.displayName = 'TextArea';
